/**
 * Browser file downloads.
 *
 * Two flavours, because there are two genuinely different situations.
 *
 * `downloadBlob` saves bytes you already have — a CSV built in the browser.
 *
 * `downloadFromApi` fetches from the API first, and exists because a plain
 * `<a href="/api/...">` cannot authenticate. Auth here is a Bearer header applied
 * by the axios interceptor, not a cookie the browser would attach on its own, so
 * a link to a protected route gets a 401 and downloads nothing. Two such links
 * already exist in the app (`AnalyticsPage` points anchors at the CSV export
 * routes) and neither of them works.
 */

import { apiClient } from "@/api/client";

/** Hand bytes to the browser as a file. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Fetch a file from an authenticated API route and save it.
 *
 * @param url - API path, e.g. `/api/v1/sales-orders/{id}/invoice.pdf`.
 * @param filename - What to call the saved file.
 * @throws An Error carrying the server's own message, where there is one.
 */
export async function downloadFromApi(
  url: string,
  filename: string,
): Promise<void> {
  try {
    const response = await apiClient.get(url, { responseType: "blob" });
    downloadBlob(response.data as Blob, filename);
  } catch (err) {
    throw new Error(await errorMessageFromBlob(err));
  }
}

/**
 * Read an error message out of a failed blob request.
 *
 * With `responseType: "blob"` axios hands back the *error body as a Blob* too, so
 * the usual `error.response.data.detail` is `undefined` and any message the
 * server took the trouble to write is thrown away. The PDF route's 503 says
 * exactly which system libraries are missing; without this the user would see
 * "failed" and nothing else.
 */
async function errorMessageFromBlob(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.detail === "string") return parsed.detail;
    } catch {
      // Not JSON — fall through to the generic message rather than showing the
      // user a fragment of an HTML error page.
    }
  } else if (typeof (data as { detail?: unknown })?.detail === "string") {
    // A non-blob error body, which happens when the request fails before the
    // response type is applied.
    return (data as { detail: string }).detail;
  }

  return "The download failed. Please try again.";
}
